import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateWeeklyPlan, getLastWeekData } from '../utils/weeklyPlanner';
import { getUserMeasurementHistory, getUserProfile } from '../utils/userDataManager';
import { getCurrentWeekNutrition, calculateNutritionSummary } from '../utils/nutritionManager';
import './WeeklyPlanner.css';

function WeeklyPlanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [checkInData, setCheckInData] = useState(null);

  useEffect(() => {
    // Check if we have check-in data from navigation state
    if (location.state?.checkInData) {
      setCheckInData(location.state.checkInData);
    }
  }, [location]);

  const handleGeneratePlan = async () => {
    setLoading(true);
    setError(null);
    setGeneratedPlan(null);

    try {
      // Load user profile for AI personalization
      const userProfile = await getUserProfile();
      console.log('Loaded user profile for AI:', userProfile);

      // Collect last week's data
      const data = getLastWeekData();

      // Add check-in data if available
      if (checkInData) {
        data.currentCheckIn = checkInData;
      }

      // Add measurement history
      data.measurementHistory = getUserMeasurementHistory();

      // Load last week's nutrition data
      const nutritionEntries = await getCurrentWeekNutrition();
      console.log('Loaded nutrition entries:', nutritionEntries);

      // Get nutrition goals from today's workout (or use defaults)
      const workoutPlans = JSON.parse(localStorage.getItem('workoutPlans') || '{}');
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = days[new Date().getDay()];
      const todayWorkout = workoutPlans[dayName] || {};

      const nutritionGoals = {
        calorieGoal: parseInt(todayWorkout.calorieGoal) || 2000,
        proteinGoal: parseInt(todayWorkout.proteinGoal) || 150
      };

      // Calculate nutrition summary
      const nutritionSummary = calculateNutritionSummary(nutritionEntries, nutritionGoals);
      console.log('Nutrition summary:', nutritionSummary);

      data.nutritionSummary = nutritionSummary;
      data.nutritionGoals = nutritionGoals;

      setWeeklyData(data);

      // Generate plan from Claude with user profile
      const planText = await generateWeeklyPlan(data, userProfile);

      // Validate response
      if (!planText || planText.trim().length === 0) {
        throw new Error('Empty response from Claude API');
      }

      // Parse JSON from Claude's response
      // Claude might wrap it in ```json ... ```, so we need to extract it
      let jsonStr = planText;
      const jsonMatch = planText.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        jsonStr = jsonMatch[1];
      }

      let plan;
      try {
        plan = JSON.parse(jsonStr);
      } catch (parseError) {
        throw new Error(`Failed to parse Claude response: ${parseError.message}`);
      }

      setGeneratedPlan(plan);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = () => {
    if (!generatedPlan) return;

    // Save to localStorage as the new workout plan
    const { weekSummary = '', ...weekPlan } = generatedPlan;
    localStorage.setItem('workoutPlans', JSON.stringify(weekPlan));
    localStorage.setItem('lastPlanUpdate', new Date().toISOString());
    localStorage.setItem('lastPlanSummary', weekSummary);

    alert('✅ New weekly plan saved! Your workouts are now updated.');
    navigate('/');
  };

  return (
    <div className="weekly-planner-screen">
      <div className="planner-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
        <h1 className="planner-title">🧠 AI Weekly Planner</h1>
        <p className="planner-subtitle">
          Let Claude analyze your progress and design next week's training
        </p>
      </div>

      {!generatedPlan && !checkInData && (
        <div className="check-in-prompt-card">
          <h3 className="prompt-title">📊 Weekly Check-In Recommended</h3>
          <p className="prompt-text">
            For the best results, complete your weekly check-in first. Claude will use your latest body measurements, blood pressure, and progress notes to design a better program.
          </p>
          <button
            className="check-in-start-button"
            onClick={() => navigate('/weekly-check-in')}
          >
            📋 Complete Check-In First
          </button>
          <p className="or-text">or</p>
        </div>
      )}

      {!generatedPlan && (
        <div className="data-preview-card">
          <h3 className="preview-title">📊 What Claude will analyze:</h3>
          <ul className="preview-list">
            <li>✓ All workouts from last 7 days (weights, reps, difficulty)</li>
            <li>✓ Missed workout sessions</li>
            {checkInData ? (
              <>
                <li>✓ THIS WEEK'S body measurements (weight, body fat, biceps, chest, waist, thighs)</li>
                <li>✓ Cardiovascular health (blood pressure, resting heart rate)</li>
                <li>✓ Fat-free mass and muscle gain tracking</li>
              </>
            ) : (
              <li>✓ Previous body measurements and trends</li>
            )}
            <li>✓ Pull-up and push-up progress</li>
            <li>✓ Your training goals</li>
          </ul>
        </div>
      )}

      {weeklyData && !generatedPlan && !loading && (
        <div className="last-week-summary">
          <h3 className="summary-title">Last Week's Performance</h3>
          <div className="summary-stats">
            <div className="stat-box">
              <span className="stat-number">{weeklyData.lastWeekWorkouts.length}</span>
              <span className="stat-label">Workouts Completed</span>
            </div>
            <div className="stat-box">
              <span className="stat-number">{weeklyData.missedWorkouts.length}</span>
              <span className="stat-label">Workouts Missed</span>
            </div>
          </div>
          {weeklyData.missedWorkouts.length > 0 && (
            <p className="missed-text">Missed: {weeklyData.missedWorkouts.join(', ')}</p>
          )}
        </div>
      )}

      <button
        className="generate-button"
        onClick={handleGeneratePlan}
        disabled={loading}
      >
        {loading ? '🤔 Claude is analyzing and creating your plan...' : '🚀 Generate Next Week\'s Plan'}
      </button>

      {error && (
        <div className="error-card">
          <p className="error-text">⚠️ {error}</p>
          {error.includes('API key') && (
            <button
              className="settings-button"
              onClick={() => navigate('/settings')}
            >
              Go to Settings
            </button>
          )}
        </div>
      )}

      {generatedPlan && (
        <div className="plan-result">
          <div className="plan-summary-card">
            <h3 className="summary-header">💡 Claude's Analysis</h3>
            <p className="summary-text">{generatedPlan.weekSummary}</p>
          </div>

          <div className="plan-preview">
            <h3 className="preview-header">📅 Your New Weekly Plan</h3>
            {Object.entries(generatedPlan).filter(([day]) => day !== 'weekSummary').map(([day, workout]) => (
              <div key={day} className="day-preview-card">
                <div className="day-preview-header">
                  <h4 className="day-name">{day.charAt(0).toUpperCase() + day.slice(1)}</h4>
                  <span className="day-time">{workout.time}</span>
                </div>
                <p className="day-workout-name">{workout.name}</p>
                <p className="day-focus">🎯 {workout.focus}</p>
                <p className="day-exercises">{workout.exercises.length} exercises • {workout.location}</p>
              </div>
            ))}
          </div>

          <div className="action-buttons">
            <button className="save-plan-button" onClick={handleSavePlan}>
              ✅ Save & Use This Plan
            </button>
            <button className="regenerate-button" onClick={handleGeneratePlan}>
              🔄 Generate Different Plan
            </button>
          </div>

          <div className="warning-note">
            <p>⚠️ Saving will replace your current workout plan. You can always generate a new one.</p>
          </div>
        </div>
      )}

      {!generatedPlan && (
        <div className="info-card">
          <h3 className="info-header">How it works:</h3>
          <ol className="info-steps">
            <li>Claude reviews your last 7 days of training data</li>
            <li>Analyzes difficulty ratings, weights used, and completion rates</li>
            <li>Considers your body measurements and goal progress</li>
            <li>Designs a progressive overload plan for next week</li>
            <li>Adjusts exercises based on what's working and what's not</li>
          </ol>
        </div>
      )}
    </div>
  );
}

export default WeeklyPlanner;
